import { m } from "#/paraglide/messages";
import { Button, Checkbox, CheckboxGroup, Field, FieldError, cn } from "@beweegbaas/ui";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { submitUpdateRapportKlassen } from "#/modules/rapporten/rapporten.functions";
import { RapportInstellingenDialog } from "#/modules/rapporten/components/card.rapport-preferences";
import { RapportenFlowSummary } from "#/modules/rapporten/rapporten-flow-summary";
import { step2Schema } from "#/modules/rapporten/rapporten-flow.steps";
import { useRapportenFlow } from "#/modules/rapporten/use-rapporten-flow";
import { useAppForm } from "#/modules/util.forms/form";

type SubmitMeta = { submitAction: "update" | "proceed" };
const defaultSubmitMeta: SubmitMeta = { submitAction: "update" };
const reportsRouteApi = getRouteApi("/$locale/{-$schoolyear}/_authenticated/$school/reports");

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/2/",
)({
  component: ReportsStep2Page,
  errorComponent: ReportsStep2Error,
});

function ReportsStep2Page() {
  const { locale, schoolyear, school, id } = Route.useParams();
  const { rapport } = useRapportenFlow();
  const { klassen } = reportsRouteApi.useLoaderData();
  const router = useRouter();
  const submitUpdateKlassen = useServerFn(submitUpdateRapportKlassen);

  const form = useAppForm({
    defaultValues: {
      klassen: rapport?.klassen?.map((klas) => klas.id) ?? [],
    },
    validators: {
      onChange: step2Schema,
    },
    onSubmitMeta: defaultSubmitMeta,
    onSubmit: async ({ value, meta }) => {
      try {
        await submitUpdateKlassen({
          data: {
            locale,
            schoolyear,
            school,
            rapportId: id,
            klassen: value.klassen,
            intent: meta.submitAction,
          },
        });
      } catch {
        // toastErrorMiddleware already surfaced the error
        return;
      }
      await router.invalidate();
    },
  });

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          className={cn("max-md:self-start max-sm:mb-4")}
          fallback={{ from: Route.fullPath, to: "../1" }}
        />
        <Page.Title>Welke klassen?</Page.Title>

        <Page.Header.Actions>
          <Page.Header.SecondaryActions>
            <RapportInstellingenDialog />
          </Page.Header.SecondaryActions>
          <Page.Header.MainAction>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit]) => (
                <Button
                  className="max-sm:w-full"
                  type={"button"}
                  size={"xl"}
                  onClick={() => {
                    void form.handleSubmit({ submitAction: "proceed" });
                  }}
                  disabled={!canSubmit}
                >
                  {m["common.actions.next"]()}
                  <IconMaterialSymbolsArrowRightAltRounded />
                </Button>
              )}
            />
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <Page.Container>
        <Page.Body>
          <Page.SidebarLayout>
            <form className="grow" onSubmit={(e) => e.preventDefault()}>
              <form.Field
                name="klassen"
                children={(field) => {
                  const isInvalid = !field.state.meta.isValid && field.state.meta.isTouched;
                  const errorMessage =
                    isInvalid && field.state.meta.errors[0]?.message
                      ? field.state.meta.errors[0].message
                      : undefined;

                  return (
                    <Field data-invalid={isInvalid}>
                      <CheckboxGroup
                        aria-label={m["rapporten.selecteer_groep"]()}
                        value={field.state.value ?? undefined}
                        onValueChange={(value) => {
                          field.handleChange(value);
                          void form.handleSubmit({ submitAction: "update" });
                        }}
                      >
                        {klassen?.map((classes) => {
                          const isSelected =
                            field.state.value?.includes(classes.id.toString()) ?? false;
                          return (
                            <label
                              key={classes.id}
                              className={cn(
                                `bg-card flex cursor-pointer items-start gap-6 rounded-lg
                              px-6 py-5 text-sm shadow ring-2
                              transition first:rounded-t-lg
                              last:rounded-b-lg first:last:rounded-lg`,
                                {
                                  "ring-transparent": !isSelected,
                                  [`relative z-10 bg-linear-to-tr from-blue-100
                                to-blue-50 ring-2 ring-blue-800 dark:bg-none
                                dark:bg-blue-950/50 dark:ring-blue-400`]: isSelected,
                                },
                              )}
                            >
                              <Checkbox value={classes.id.toString()} />
                              <div className={"flex grow justify-between"}>
                                <div className={"-mt-1 flex flex-col gap-0"}>
                                  <strong
                                    className={cn("font-heading text-base", {
                                      "text-blue-600 dark:text-blue-300": isSelected,
                                    })}
                                  >
                                    Groep {classes.name}{" "}
                                  </strong>

                                  <em
                                    className={cn({
                                      "text-blue-600 dark:text-blue-300": isSelected,
                                    })}
                                  >
                                    leerjaar {classes.leerjaren.join("/")}
                                  </em>
                                </div>

                                <em>
                                  {m["les.start.student_count"]({
                                    count: classes.leerlingCount,
                                  })}
                                </em>
                              </div>
                            </label>
                          );
                        })}
                      </CheckboxGroup>
                      {errorMessage && <FieldError>{errorMessage}</FieldError>}
                    </Field>
                  );
                }}
              />
            </form>

            <Page.Sidebar className="max-lg:hidden">
              <RapportenFlowSummary />
            </Page.Sidebar>
          </Page.SidebarLayout>
        </Page.Body>
      </Page.Container>
    </Page>
  );
}

function ReportsStep2Error({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
